import useTranslation from "../client/hooks/useTranslation";
import { cn } from "../client/utils";
import type { BalanceReconciliation } from "./balanceReconciliation";

// Réserve d'ÉCART entre le solde broker et le journal des trades.
//
// POURQUOI CE COMPOSANT EXISTE
// `BalanceFreshnessHint` répond à « ce chiffre date-t-il ? ». Celui-ci répond à
// une question différente, et jusqu'ici sans réponse : « pourquoi ce chiffre ne
// correspond-il pas à mes trades ? ».
//
// Cas réel : un compte Apex affichait, l'un sous l'autre, un solde de 50 000
// (broker, relu au plus une fois par heure), un PnL de +1 020,70 (somme des
// trades, temps réel) et un objectif à +0 / 3 000 (calculé sur le solde
// broker). Trois chiffres mathématiquement incompatibles, présentés sans un mot
// d'explication, pendant que le calendrier affichait le vrai PnL. Rien n'était
// faux au sens du code — mais l'écran était incohérent pour qui le lit.
//
// `balanceGap` était calculé côté serveur depuis l'origine et n'était affiché
// NULLE PART. Ce composant est ce chaînon manquant, sur le modèle exact de
// BalanceFreshnessHint.
//
// CE QU'IL NE FAIT PAS
// Il ne change AUCUN chiffre et ne corrige aucun calcul : l'arbitrage « le
// broker fait autorité » reste entier dans displayBalance/engineBalance, où il
// protège d'un incident déjà vécu (« objectif atteint » sur un compte cramé).
// On rend l'écart LISIBLE, on ne le masque pas et on ne le contourne pas.

export default function BalanceGapHint({
  reconciliation,
  formatCurrency,
  className,
}: {
  reconciliation: BalanceReconciliation | null | undefined;
  /** Formateur monétaire de l'écran appelant, pour rester homogène. */
  formatCurrency: (value: number) => string;
  className?: string;
}) {
  const t = useTranslation();

  // Régime nominal, compte manuel, ou écart sous le seuil de bruit : rien à
  // signaler. Une mention permanente s'apprend à s'ignorer.
  if (!reconciliation || reconciliation.kind === "none" || reconciliation.gap === null) {
    return null;
  }

  const amount = formatCurrency(Math.abs(reconciliation.gap));
  const isAhead = reconciliation.kind === "journal_ahead";

  const label = (
    isAhead
      ? t.tradingAccounts.balanceGapJournalAhead
      : t.tradingAccounts.balanceGapJournalIncomplete
  ).replace("{gap}", amount);

  const hint = isAhead
    ? t.tradingAccounts.balanceGapJournalAheadHint
    : t.tradingAccounts.balanceGapJournalIncompleteHint;

  // Un journal en avance se résorbe tout seul au prochain relevé : c'est une
  // information, pas une alerte. Un journal incomplet ne se résorbe PAS seul et
  // fausse les règles du compte : il mérite la couleur d'avertissement.
  const tone = isAhead
    ? "text-muted-foreground"
    : "text-amber-500";
  const dot = isAhead ? "bg-muted-foreground/60" : "bg-amber-500";

  return (
    <span
      title={hint}
      className={cn(
        "inline-flex items-center gap-1 text-[10px] font-medium whitespace-nowrap",
        tone,
        className,
      )}
    >
      <span aria-hidden="true" className={cn("w-1.5 h-1.5 rounded-full shrink-0", dot)} />
      {label}
    </span>
  );
}
